import { createSlice, PayloadAction, Action } from '@reduxjs/toolkit';
import { AsiteFolderTree, AsiteDocument } from '../../types/Asite';
import { apiCallBegan } from '../api/action';
import { AppThunk } from '..';
import { msalInstance } from '../../../auth';

export interface AsiteState {
    data: AsiteFolderTree[];
    applicationId: number;
    currentFolderId: number;
    currentDocuments: AsiteDocument[];
    loading: boolean;
    error: string;
}

let currentNode: AsiteFolderTree | null = null;
const initialState: AsiteState = {
    data: [],
    applicationId: 1,
    currentFolderId: 0,
    currentDocuments: [],
    loading: false,
    error: '',
};

const asiteSlice = createSlice({
    name: 'asite',
    initialState,
    reducers: {
        asiteDataRequested(state: AsiteState, action: PayloadAction<number | undefined>) {
            state.loading = true;
            state.error = '';

            if (action.payload) {
                state.currentFolderId = action.payload;
            }
        },

        asiteRequestFailed(state: AsiteState, action: PayloadAction<string>) {
            state.loading = false;
            state.error = action.payload;
        },

        asiteFoldersReceived(state: AsiteState, action: PayloadAction<AsiteFolderTree[]>) {
            findCurrentNode(state.currentFolderId, state.data);
            state.loading = false;
            if (currentNode) {
                currentNode.items = action.payload;
                currentNode.expanded = true;
                currentNode = null;
            } else {
                state.data = action.payload;
            }
        },

        expandTriggered(state: AsiteState, action: PayloadAction<number>) {
            state.currentFolderId = action.payload;
            findCurrentNode(state.currentFolderId, state.data);

            console.log(currentNode);
            if (currentNode) {
                currentNode.expanded = !currentNode.expanded;
                currentNode = null;
            }
        },

        asiteDocumentsReceived(state: AsiteState, action: PayloadAction<AsiteDocument[]>) {
            findCurrentNode(state.currentFolderId, state.data);

            state.loading = false;
            if (currentNode) {
                currentNode.documents = action.payload;
                currentNode = null;
            }

            state.currentDocuments = action.payload;
        },

        currentDocumentsChanged(state: AsiteState, action: PayloadAction<number>) {
            state.currentFolderId = action.payload;
            findCurrentNode(state.currentFolderId, state.data);

            if (currentNode) {
                state.currentDocuments = currentNode.documents ? currentNode.documents : [];
                currentNode = null;
            }
        },

        inconsistentStateCleaned(state: AsiteState, action: Action) {
            state.currentDocuments = [];
            clearExpands(state.data);
        },
        treeDataClean(state: AsiteState) {
            state.data = [];
            state.applicationId = 1;
            state.currentDocuments = [];
            state.loading = false;
            state.error = '';
            state.currentFolderId = 0;
        },
    },
});

export const asiteReducer = asiteSlice.reducer;
const {
    asiteDataRequested,
    asiteRequestFailed,
    asiteFoldersReceived,
    asiteDocumentsReceived,
    expandTriggered,
    currentDocumentsChanged,
    inconsistentStateCleaned,
    treeDataClean,
} = asiteSlice.actions;

export type AsiteActionTypes =
    | ReturnType<typeof asiteDataRequested>
    | ReturnType<typeof asiteRequestFailed>
    | ReturnType<typeof asiteFoldersReceived>
    | ReturnType<typeof asiteDocumentsReceived>
    | ReturnType<typeof expandTriggered>
    | ReturnType<typeof currentDocumentsChanged>
    | ReturnType<typeof inconsistentStateCleaned>
    | ReturnType<typeof treeDataClean>;

// Thunks
export const initiateFolders = (): any => async (dispatch:any, getState:any) => {
    if (getState().asite.data.length > 0) return;

    dispatch(asiteDataRequested());
    const applicationId = getState().asite.applicationId;
    const id = msalInstance.getActiveAccount()?.localAccountId!;

    // Request folders
    return dispatch(
        apiCallBegan({
            url: '/api/Asite/GetAsiteFoldersTreeByIdParent',
            method: 'GET',
            safe: false,
            params: {
                id,
                applicationId,
            },
            onSuccess: asiteFoldersReceived.type,
            onError: asiteRequestFailed.type,
        }),
    );
};

export const loadFolders = (folderId: number): AppThunk => async (dispatch, getState) => {
    findCurrentNode(folderId, getState().asite.data);
    if ((currentNode?.hasChildren && currentNode.items) || (!currentNode?.hasChildren && currentNode?.documents)) {
        currentNode = null;
        dispatch(expandTriggered(folderId));
        return;
    }

    const applicationId = getState().asite.applicationId;
    const id = msalInstance.getActiveAccount()?.localAccountId!;

    currentNode = null;
    dispatch(asiteDataRequested(folderId));
    return dispatch(
        apiCallBegan({
            url: '/api/Asite/GetAsiteFoldersTreeByIdParent',
            method: 'GET',
            safe: false,
            params: {
                id,
                applicationId,
                folderId,
            },
            onSuccess: asiteFoldersReceived.type,
            onError: asiteRequestFailed.type,
        }),
    );
};

export const loadDocuments = (folderId: number): AppThunk => async (dispatch, getState) => {
    findCurrentNode(folderId, getState().asite.data);
    if ((currentNode?.hasChildren && currentNode.items) || (!currentNode?.hasChildren && currentNode?.documents)) {
        currentNode = null;
        dispatch(currentDocumentsChanged(folderId));
        return;
    }

    currentNode = null;
    dispatch(asiteDataRequested(folderId));
    return dispatch(
        apiCallBegan({
            url: '/api/Asite/GetDocumentsForFolder',
            method: 'POST',
            safe: true,
            body: {
                FolderId: folderId,
            },
            onSuccess: asiteDocumentsReceived.type,
            onError: asiteRequestFailed.type,
        }),
    );
};

export const resetTreeData = () => treeDataClean();

export const cleanupInconsistentState = () => inconsistentStateCleaned();

// Helpers
const findCurrentNode = (folderId: number, asiteFolderTree: AsiteFolderTree[]) => {
    if (currentNode) return;

    asiteFolderTree.forEach((asiteFolderTreeNode) => {
        if (asiteFolderTreeNode.folderId === folderId.toString()) {
            currentNode = asiteFolderTreeNode;
            return;
        }

        if (asiteFolderTreeNode.items) {
            findCurrentNode(folderId, asiteFolderTreeNode.items);
        }
    });
};

const clearExpands = (asiteFolderTree: AsiteFolderTree[]) => {
    asiteFolderTree.forEach((asiteFolderTreeNode) => {
        asiteFolderTreeNode.expanded = false;

        if (asiteFolderTreeNode.items) {
            clearExpands(asiteFolderTreeNode.items);
        }
    });
};
